import { forwardRef } from 'react'
import styles from './Input.module.scss'

/**
 * Text/number input primitive with optional prefix and suffix addons.
 *
 * The visible border and focus ring live on the wrapper `<div>`, not the
 * `<input>` element itself. The `<input>` is transparent and fills the wrapper.
 * This allows arbitrary prefix/suffix content to appear visually "inside" the
 * field while remaining selectable text.
 *
 * Prefix and suffix are rendered as non-interactive `<span>` elements.
 * For interactive addons (e.g. clear button) use the parent component layer.
 *
 * @param {object}   props
 * @param {string|number} props.value
 * @param {(v: string) => void} props.onChange  - always fires raw string
 * @param {string}  [props.type='text']
 * @param {string}  [props.placeholder]
 * @param {React.ReactNode} [props.prefix]      - e.g. "od", "do"
 * @param {React.ReactNode} [props.suffix]      - e.g. "zł", "m²"
 * @param {boolean} [props.isActive=false]      - actively filtering
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.trimOnBlur=false]     - trim whitespace from value on blur
 * @param {string}  [props.id]
 * @param {string}  [props.name]                - falls back to id if omitted
 * @param {string}  [props.className]           - extra class on wrapper
 * @param {number}  [props.min]
 * @param {number}  [props.max]
 * @param {number}  [props.step]
 * @param {function} [props.onBlur]
 * @param {function} [props.onFocus]
 * @param {function} [props.onKeyDown]
 */
export const Input = forwardRef(function Input(
  {
    value,
    onChange,
    type = 'text',
    placeholder,
    prefix,
    suffix,
    isActive = false,
    disabled = false,
    trimOnBlur = false,
    id,
    name,
    className,
    min,
    max,
    step,
    onBlur,
    onFocus,
    onKeyDown,
  },
  ref,
) {
  // Arrow key stepping for type="number" — browser native stepping may be
  // suppressed by the WP reset, so we implement it explicitly.
  function handleKeyDown(e) {
    if (type === 'number' && onChange && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault()
      const effectiveStep = step != null ? Number(step) : 1
      const current = parseFloat(value) || 0
      const raw = current + effectiveStep * (e.key === 'ArrowUp' ? 1 : -1)
      // Round to step's decimal precision to avoid floating-point drift.
      const decimals = String(effectiveStep).split('.')[1]?.length ?? 0
      let next = parseFloat(raw.toFixed(decimals))
      if (min != null) next = Math.max(next, Number(min))
      if (max != null) next = Math.min(next, Number(max))
      onChange(String(next))
    }
    onKeyDown?.(e)
  }
  return (
    <div
      className={[
        styles.wrapper,
        isActive ? styles.active : '',
        disabled ? styles.disabled : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {prefix != null && (
        <span className={styles.prefix} aria-hidden="true">
          {prefix}
        </span>
      )}

      <input
        ref={ref}
        id={id}
        name={name ?? id}
        className={styles.input}
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={(e) => {
            if (trimOnBlur && onChange) {
              const trimmed = e.target.value.trim()
              if (trimmed !== e.target.value) onChange(trimmed)
            }
            onBlur?.(e)
          }}
        onFocus={onFocus}
        onKeyDown={handleKeyDown}
        // IMPORTANT: inline styles to beat WP reset.
        // `#bim-cadaplus-app input { all: unset }` is specificity (1,0,1);
        // CSS module classes are (0,1,0) — they lose. Inline always wins.
        // This also restores `flex: 1` and `min-width: 0` which `all: unset`
        // resets to their initial values (0 1 auto → input collapses).
        style={{
          padding: `0 0.5em 0 ${prefix != null ? '0.5em' : '1em'}`,
          flex: 1,
          minWidth: 0,
          ...(type === 'number' ? { MozAppearance: 'textfield' } : {}),
        }}
      />

      {suffix != null && (
        <span className={styles.suffix} aria-hidden="true">
          {suffix}
        </span>
      )}
    </div>
  )
})
